interface ColorGenerationOptions {
  mainGroupColumnName: string;
  subgroupColumnName: string;
  seed?: number;
  baseColor?: string;
  transparency?: number;
  thickBorders?: boolean;
}

interface ColorResult {
  mainGroupColorArray: { field: string; color: string }[];
  subGroupColorArray: { field: string; color: string }[];
  mainGroupIndexChanges: number[];
  subgroupIndexChanges: number[];
}

const generateColors = (data: any[], options: ColorGenerationOptions): ColorResult => {
  const {
    mainGroupColumnName = '',
    subgroupColumnName = '',
    seed = 0,
    baseColor = '#FFFFFFFF',
    transparency = 0.3,
    thickBorders = true,
  } = options;

  let optionsCopy: ColorGenerationOptions = { ...options };

  const ColorArray: { field: string; color: string }[] = [];
  const subGroupColourArray: { field: string; color: string }[] = [];
  const indexChanges: number[] = [];
  const subgroupIndexChanges: number[] = [];

  const deterministicRandom = () => {
    // A simple deterministic random number generator
    const x = Math.sin(optionsCopy.seed++) * 10000;
    return x - Math.floor(x);
  };

  const getRandomPercentage = (index: number): number => {
    const calculatedPercentage = (deterministicRandom() * 20 - 20);
    return calculatedPercentage;
  };

  const generateRandomColor = (index: number): string => {
    const randomBrightColor = (): string => {
      const minBrightness = 150;
      const randomChannelValue = (): number => Math.floor(0.05 * index * (255 - minBrightness) + minBrightness);
      const brightColor = [randomChannelValue(), randomChannelValue(), randomChannelValue()];
      return '#' + brightColor.map(value => value.toString(16).padStart(2, '0')).join('');
    };

    const hexToRgb = (hex: string): number[] => {
      hex = hex.slice(1);
      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return [r, g, b];
    };

    const rgbToHex = (rgb: number[]): string => {
      return '#' + rgb.map(value => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('');
    };

    const blendColors = (color1: number[], color2: number[], transparency: number): number[] => {
      const blendedColor = color1.map((channel, index) => {
        const mixedChannel = Math.round(channel * (1 - transparency) + color2[index] * transparency);
        return Math.min(255, mixedChannel);
      });
      return blendedColor;
    };

    const baseRgb = hexToRgb(baseColor);
    const randomRgb = hexToRgb(randomBrightColor());

    const blendedRgb = blendColors(baseRgb, randomRgb, transparency);

    const blendedHexColor = rgbToHex(blendedRgb);
    const finalHexColor = `${blendedHexColor}${Math.round(transparency * 255).toString(16).toUpperCase()}`;
    return finalHexColor;
  };

  const generateSubgroupColor = (mainGroupColor: string, index: number): string => {
    const hexToRgb = (hex: string): number[] => {
      const bigint = parseInt(hex.slice(1), 16);
      const r = (bigint >> 24) & 255;
      const g = (bigint >> 16) & 255;
      const b = (bigint >> 8) & 255;
      const a = (bigint & 255) / 255;
      return [r, g, b, a];
    };

    const rgbToHex = (rgb: number[]): string => {
      const alpha = Math.round(rgb[3] * 255);
      return `#${rgb[0].toString(16).padStart(2, '0')}${rgb[1].toString(16).padStart(2, '0')}${rgb[2].toString(16).padStart(2, '0')}${alpha.toString(16).padStart(2, '0')}`;
    };

    const rgbValues = hexToRgb(mainGroupColor);

    const manipulatedRgbValues = rgbValues.map((value, index) => {
      if (index === 3) return value;
      return Math.round(value * (1 + getRandomPercentage(index) / 100));
    });

    const subgroupColor = rgbToHex(manipulatedRgbValues);

    return subgroupColor;
  };

  data?.forEach((row, index) => {
    const mainGroupValue = row[mainGroupColumnName];
    const subgroupValue = row[subgroupColumnName];

    if (index === 0 || mainGroupValue !== data[index - 1][mainGroupColumnName]) {
      const colR = generateRandomColor(index);
      ColorArray.push({ field: mainGroupValue, color: colR });
      indexChanges.push(index);
    }

    const mainGroupColor = ColorArray.find(item => item.field === mainGroupValue)?.color;

    if (subgroupValue !== null && (index === 0 || subgroupValue !== data[index - 1][subgroupColumnName])) {
      const previousSubgroupValue = data[index - 1] ? data[index - 1][subgroupColumnName] : null;
      if (previousSubgroupValue !== subgroupValue && mainGroupColor) {
        subGroupColourArray.push({ field: subgroupValue, color: generateSubgroupColor(mainGroupColor, index) });
        subgroupIndexChanges.push(index);
      }
    }
  });

  const result: ColorResult = {
    mainGroupColorArray: ColorArray,
    subGroupColorArray: subGroupColourArray,
    mainGroupIndexChanges: indexChanges,
    subgroupIndexChanges: subgroupIndexChanges,
  };
  console.log("color generator called!!!");
  return result;
};

export default generateColors;

